/**
 * Display helpers — no hardcoded dollar amounts.
 */

export const OPS_ERROR_RU = {
  rate_limited_interval: 'Слишком часто — подождите несколько секунд',
  rate_limited_window: 'Лимит действий за окно — подождите',
  origin_forbidden: 'Запрос с чужого origin отклонён',
  unit_not_allowed: 'Unit не в allowlist',
  confirm_mismatch: 'Токен подтверждения не совпал',
  action_not_allowed: 'Действие не разрешено',
  confirm_required: 'Введите токен подтверждения',
  unit_unavailable_for_mode: 'Unit недоступен для этого режима',
  unauthorized: 'Нужна авторизация',
  horizon_invalid: 'Неверный горизонт (нужен 15m или 5m)',
  asset_invalid: 'Неверная монета (btc/eth/sol/bnb)',
  strategy_id_invalid: 'Неверный id стратегии',
  strategy_not_in_horizon: 'Стратегия не в списке этого горизонта',
  settings_write_failed: 'Не удалось записать yaml (права lunes-gate / g+w)',
  settings_missing: 'yaml стратегий не найден',
  settings_empty: 'yaml пустой — запись отменена',
  settings_read_failed: 'yaml не читается',
  strategies_block_missing: 'В yaml нет блока strategies',
  live_disabled_block_missing: 'В yaml нет блока live.multi_strat.disabled',
  dogon_block_missing: 'В yaml нет блока dogon',
  dogon_enabled_unreadable: 'Поле dogon.enabled не читается',
  kill_state_write_failed: 'Не удалось сбросить dogon hole в kill_state',
  enabled_required: 'Нужен флаг enabled',
  systemctl_failed: 'systemctl start/stop не удался',
  systemctl_unavailable: 'systemctl недоступен',
  systemctl_timeout: 'systemctl таймаут',
  live_wallet_mutex:
    'Сейчас включён другой live-бот (Rust / CES). Выключи его руками, потом включай этот.',
  live_state_unreadable:
    'Не удалось прочитать состояние live (control/yaml). Включать нельзя — fail closed.',
  account_not_found: 'Клиент не найден',
  imitation_not_friend: 'Имитация Георга — не авария друга. Отзыв только для клиента.',
  imitation_cannot_live: 'Имитацию нельзя переключить в live',
  imitation_no_live_yaml: 'У имитации нет live yaml',
  yaml_missing: 'Yaml друга ещё нет — появятся после клона live / ключей',
  yaml_unreadable: 'Yaml клиента не читается (права ACL после isolation)',
  halt_write_failed: 'Halt не записался (права ACL). wanted_on выкл, но KILL_SWITCH может отсутствовать.',
  halt_clear_failed: 'Не удалось снять halt (ACL). Юнит не стартовали.',
  operator_live_yaml_forbidden: 'Нельзя писать yaml живой торговли Георга',
  yaml_path_forbidden: 'Путь yaml вне каталога клиента',
  cannot_go_live: 'Клиента нельзя включить в live без ключей',
  telegram_id_invalid: 'Неверный telegram_id',
  unexpected_revoke: 'Выкл торговли не должен снимать с белого списка',
}

/**
 * @param {number | null | undefined} n
 * @param {{ digits?: number, signed?: boolean }} [opts]
 */
export function fmtNum(n, opts = {}) {
  if (n == null || n === '' || !Number.isFinite(Number(n))) return '—'
  const digits = opts.digits ?? 2
  const v = Number(n)
  const body = Math.abs(v).toLocaleString('ru-RU', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
  if (opts.signed) {
    if (v > 0) return `+${body}`
    if (v < 0) return `−${body}`
    return body
  }
  return v < 0 ? `−${body}` : body
}

/**
 * Settled win rate (0–1 fraction) → percentage string.
 * Prefer server `win_rate`; fall back to wins/(wins+losses).
 * @param {number | null | undefined} winRate
 * @param {{ wins?: number | null, losses?: number | null }} [counts]
 */
export function fmtWinRate(winRate, counts = {}) {
  if (winRate != null && Number.isFinite(Number(winRate))) {
    return `${Math.round(Number(winRate) * 100)}%`
  }
  const w = Number(counts.wins)
  const l = Number(counts.losses)
  if (!Number.isFinite(w) || !Number.isFinite(l) || w + l <= 0) return '—'
  return `${Math.round((w / (w + l)) * 100)}%`
}

/**
 * Bankroll object → display strings (no $ prefix).
 * @param {Record<string, unknown> | null | undefined} bank
 */
export function fmtBankroll(bank) {
  if (!bank || typeof bank !== 'object') return null
  const todayGross = bank.today_gross_pnl
  const todayCosts = bank.today_costs
  const todayNet = bank.today_pnl
  let todayLine = fmtNum(todayNet, { signed: true })
  if (
    todayGross != null &&
    todayCosts != null &&
    Number.isFinite(Number(todayGross)) &&
    Number.isFinite(Number(todayCosts))
  ) {
    todayLine = `${fmtNum(todayGross, { signed: true })} gross / ${fmtNum(-Math.abs(Number(todayCosts)), { signed: true })} costs / ${fmtNum(todayNet, { signed: true })} net`
  }
  return {
    balance: fmtNum(bank.balance),
    today_pnl: todayLine,
    open_stake: fmtNum(bank.open_stake),
    starting_balance: fmtNum(bank.starting_balance),
    fees_sum: fmtNum(bank.fees_sum),
    gas_sum: fmtNum(bank.gas_sum),
    pending: bank.pending != null ? String(bank.pending) : '—',
    raw: bank,
  }
}

/**
 * WIN→«Победа» / LOSS→«Проигрыш» / VOID→«Возврат» / PENDING→«Ожидание»
 * @param {string | null | undefined} outcome
 */
export function outcomeLabelRu(outcome) {
  const o = String(outcome || 'PENDING').toUpperCase()
  if (o === 'WIN') return 'Победа'
  if (o === 'LOSS') return 'Проигрыш'
  if (o === 'VOID') return 'Возврат'
  if (o === 'SKIP') return 'Пропуск'
  return 'Ожидание'
}

/**
 * @param {string | null | undefined} outcome
 */
export function outcomeKind(outcome) {
  const o = String(outcome || 'PENDING').toUpperCase()
  if (o === 'WIN') return 'win'
  if (o === 'LOSS') return 'loss'
  if (o === 'VOID') return 'void'
  if (o === 'SKIP') return 'skip'
  return 'pending'
}

/**
 * PnL cell — empty for VOID / PENDING (ignore api pnl_est on VOID).
 * @param {{ outcome?: string, pnl_est?: number | null }} bet
 */
export function fmtBetPnl(bet) {
  if (!bet) return '—'
  const o = String(bet.outcome || '').toUpperCase()
  if (o === 'VOID' || o === 'PENDING' || o === '') return '—'
  return fmtNum(bet.pnl_est, { signed: true })
}

/**
 * Market window end (ms UTC) from API fields or `{asset}-updown-{15m|5m}-{unix_start}` slug.
 * End = slug epoch + 900s (15m) / 300s (5m). Prefer `window_end` / `expires_at` when present.
 * @param {{ slug?: string, window_end?: string | number, expires_at?: string | number } | null | undefined} bet
 * @returns {number | null}
 */
export function windowEndMs(bet) {
  if (!bet) return null
  for (const key of ['window_end', 'expires_at']) {
    const raw = bet[key]
    if (raw == null || raw === '') continue
    const d = parseToDate(/** @type {string | number} */ (raw))
    if (d && !Number.isNaN(d.getTime())) return d.getTime()
  }
  return windowEndMsFromSlug(bet.slug)
}

/**
 * @param {string | null | undefined} slug
 * @returns {number | null} window end unix ms, or null if slug not parseable
 */
export function windowEndMsFromSlug(slug) {
  const s = String(slug || '').trim()
  // `{btc|eth|sol|bnb|…}-updown-{5m|15m}-{unix}`
  const m = /^([a-z][a-z0-9]*)-updown-(5m|15m)-(\d+)$/i.exec(s)
  if (!m) return null
  const hz = m[2].toLowerCase()
  const windowSecs = hz === '5m' ? 300 : 900
  const start = Number(m[3])
  if (!Number.isFinite(start) || start <= 0) return null
  return (start + windowSecs) * 1000
}

/**
 * Compact close countdown for PENDING rows: `до закрытия m:ss`, then `закрыт`,
 * then `резолв…` (settlement lag). Once outcome is WIN/LOSS the pill replaces this.
 * @param {number | null | undefined} endMs
 * @param {number} [nowMs]
 * @returns {string | null}
 */
export function fmtCloseCountdown(endMs, nowMs = Date.now()) {
  if (endMs == null || !Number.isFinite(Number(endMs))) return null
  const left = Math.floor((Number(endMs) - nowMs) / 1000)
  if (left <= 0) {
    const overdue = Math.floor((nowMs - Number(endMs)) / 1000)
    // After ~2 min past window end, show settle wait instead of eternal «закрыт».
    if (overdue >= 120) return 'резолв…'
    return 'закрыт'
  }
  const m = Math.floor(left / 60)
  const s = left % 60
  return `до закрытия ${m}:${String(s).padStart(2, '0')}`
}

/**
 * Display timestamp in Europe/Moscow, always labeled MSK.
 * Accepts ISO-UTC (`…Z`) or unix seconds/ms.
 * @param {string | number | null | undefined} ts
 */
export function fmtDate(ts) {
  if (ts == null || ts === '') return '—'
  const d = parseToDate(ts)
  if (!d || Number.isNaN(d.getTime())) {
    return String(ts).replace('T', ' ').slice(0, 16)
  }
  const body = d.toLocaleString('ru-RU', {
    timeZone: 'Europe/Moscow',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  return `${body} MSK`
}

/**
 * @param {string | number} ts
 * @returns {Date | null}
 */
function parseToDate(ts) {
  if (typeof ts === 'number' && Number.isFinite(ts)) {
    const ms = Math.abs(ts) >= 1e12 ? ts : ts * 1000
    return new Date(ms)
  }
  const s = String(ts).trim()
  if (!s) return null
  if (/^-?\d+$/.test(s)) {
    const n = Number(s)
    const ms = Math.abs(n) >= 1e12 ? n : n * 1000
    return new Date(ms)
  }
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? null : d
}
